/**
 * Data sanitization utilities for LGPD compliance
 * Masks/redacts sensitive personal and payment data before logging
 */

/**
 * Completely redacts a value
 */
const redact = (): string => '[REDACTED]';

/**
 * Masks CPF showing only last 3 digits
 * Example: 123.456.789-01 -> ***.***.***.01
 */
const maskCPF = (cpf: string): string => {
  if (!cpf) return cpf;
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return redact();
  return `***.***.***.${cleaned.slice(-2)}`;
};

/**
 * Masks email showing only domain
 * Example: user@example.com -> u****@example.com
 */
const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return redact();
  const [local, domain] = email.split('@');
  if (!local || !domain) return redact();
  return `${local[0]}****@${domain}`;
};

/**
 * Masks phone showing only last 4 digits
 * Example: (11) 98765-4321 -> *****4321
 */
const maskPhone = (phone: string): string => {
  if (!phone) return phone;
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 4) return redact();
  return `*****${cleaned.slice(-4)}`;
};

/**
 * Masks name showing only first name
 * Example: João Silva Santos -> João ***
 */
const maskName = (name: string): string => {
  if (!name) return name;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return redact();
  return `${parts[0]} ***`;
};

/**
 * Masks CEP showing only first 5 digits
 * Example: 12345-678 -> 12345-***
 */
const maskZipCode = (zipCode: string): string => {
  if (!zipCode) return zipCode;
  const cleaned = zipCode.replace(/\D/g, '');
  if (cleaned.length !== 8) return redact();
  return `${cleaned.slice(0, 5)}-***`;
};

/**
 * Masks string showing only last 4 characters
 * Used for barCode, bankLine, PNR, etc.
 */
const maskLastFour = (value: string): string => {
  if (!value || value.length < 4) return redact();
  return `****${value.slice(-4)}`;
};

/**
 * Masks token showing only first 10 characters
 */
const maskToken = (token: string): string => {
  if (!token) return redact();
  if (token.length <= 10) return redact();
  return `${token.slice(0, 10)}...`;
};

/**
 * Sanitizes URL by removing sensitive query parameters
 */
const sanitizeUrl = (url: string): string => {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['transactionId', 'accountId', 'token', 'key'];
    sensitiveParams.forEach((param) => urlObj.searchParams.delete(param));
    return urlObj.toString();
  } catch {
    return url;
  }
};

/**
 * Sensitive field patterns and their sanitization rules
 */
const FIELD_RULES: Record<string, (value: any) => any> = {
  // Personal Identifiable Information
  cpf: maskCPF,
  email: maskEmail,
  phone: maskPhone,
  name: maskName,
  birthDate: redact,
  motherName: redact,

  // Address
  zipCode: maskZipCode,
  street: redact,
  number: redact,
  complement: redact,
  neighborhood: redact,

  // Authentication
  token: maskToken,
  pinCode: redact,
  code: redact,
  otp: redact,
  password: redact,
  secret: redact,
  Authorization: redact,
  authorization: redact,

  // Payment
  barCode: maskLastFour,
  bankLine: maskLastFour,
  pix: redact,
  pixKey: redact,
  pixQrCode: redact,
  qrCode: redact,
  paymentLink: sanitizeUrl,

  // Reservation
  pnr: maskLastFour,
};

/**
 * Pattern-based rules for field names
 */
const PATTERN_RULES = [
  { pattern: /token$/i, handler: redact },
  { pattern: /password$/i, handler: redact },
  { pattern: /secret$/i, handler: redact },
  { pattern: /^pin/i, handler: redact },
  { pattern: /authorization/i, handler: redact },
];

/**
 * Gets the appropriate sanitization handler for a field
 */
const getSanitizationHandler = (key: string): ((value: any) => any) | null => {
  // Direct match
  if (key in FIELD_RULES) {
    return FIELD_RULES[key];
  }

  // Pattern match
  const patternRule = PATTERN_RULES.find((rule) => rule.pattern.test(key));
  if (patternRule) {
    return patternRule.handler;
  }

  return null;
};

/**
 * Recursively sanitizes an object, removing/masking sensitive fields
 */
export const sanitizeObject = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitives
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item));
  }

  // Handle objects
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const handler = getSanitizationHandler(key);

    if (handler) {
      // Apply sanitization handler
      sanitized[key] = typeof value === 'string' ? handler(value) : redact();
    } else if (typeof value === 'object' && value !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeObject(value);
    } else {
      // Keep non-sensitive values
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Sanitizes data for logging - handles both objects and primitives
 */
export const sanitizeForLogging = (data: any): any => {
  if (!data) return data;

  // Handle string data (might be JSON)
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return JSON.stringify(sanitizeObject(parsed));
    } catch {
      // Not JSON, return as is (might be just a message)
      return data;
    }
  }

  // Handle objects
  return sanitizeObject(data);
};
